import React, { useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as NoPollsIcon } from "../../Assets/iconsv2/generic/noPolls.svg";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import PollResultsDisplay from "./PollResultsDisplay";
import { POLL_STATUS_LAUNCHED, POLL_STATUS_ENDED, ANSWER_TYPE_SINGLE, ANSWER_TYPE_MULTIPLE } from "../../Constants/constantValues";

const InviteesPollView = ({
  polls,
  onSubmitVote,
  isSubmitting
}) => {
  const { t } = useTranslate();
  const [selectedOptionsByPoll, setSelectedOptionsByPoll] = useState({});

  const launchedPolls = polls.filter(p => p.status === POLL_STATUS_LAUNCHED);
  const endedPolls = polls.filter(p => p.status === POLL_STATUS_ENDED);
  const attendeePolls = [...launchedPolls, ...endedPolls];

  const handleVote = (pollId) => {
    const selectedOptions = selectedOptionsByPoll[pollId] || [];
    onSubmitVote(pollId, selectedOptions, () => {
      setSelectedOptionsByPoll(prev => ({
        ...prev,
        [pollId]: []
      }));
    });
  };

  const handleOptionToggle = (poll, optionId) => {
    const currentSelections = selectedOptionsByPoll[poll.id] || [];
    
    if (poll.answer_type === ANSWER_TYPE_SINGLE) {
      setSelectedOptionsByPoll(prev => ({
        ...prev,
        [poll.id]: [optionId]
      }));
    } else {
      setSelectedOptionsByPoll(prev => ({
        ...prev,
        [poll.id]: currentSelections.includes(optionId)
          ? currentSelections.filter(id => id !== optionId)
          : [...currentSelections, optionId]
      }));
    }
  };


  if (attendeePolls.length === 0) {
    return (
      <EmptyStateWrapper>
        <NoPollsIcon width={350} height={180} opacity={0.6} />
        <EmptyStateTitle>{t("conferences.no_active_polls")}</EmptyStateTitle>
        <EmptyStateText>{t("conferences.waiting_for_polls")}</EmptyStateText>
      </EmptyStateWrapper>
    );
  }

  return (
    <AttendeeSection>
      {attendeePolls.map((poll) => (
        <AttendeePollCard key={poll.id} $isEnded={poll.status === POLL_STATUS_ENDED}>
          <AttendeePollBody $ended={poll.status === POLL_STATUS_ENDED}>
            <AttendeePollQuestion>{poll.question_text}</AttendeePollQuestion>

            {!poll.user_has_responded && poll.status === POLL_STATUS_LAUNCHED && (
              <>
                <AnswerTypeHint>
                  {poll.answer_type === ANSWER_TYPE_SINGLE
                    ? t("conferences.select_one_option")
                    : t("conferences.select_multiple_options")}
                </AnswerTypeHint>

                <VotingOptions>
                  {poll.options.map((option) => {
                    const isSelected = (selectedOptionsByPoll[poll.id] || []).includes(option.id);

                    return (
                      <VotingOption
                        key={option.id}
                        $selected={isSelected}
                        onClick={() => handleOptionToggle(poll, option.id)}
                      >
                        {poll.answer_type === ANSWER_TYPE_SINGLE ? (
                          <RadioButton $selected={isSelected}>
                            {isSelected && <RadioDot />}
                          </RadioButton>
                        ) : (
                          <CheckboxButton $selected={isSelected}>
                            {isSelected && <CheckIcon>✓</CheckIcon>}
                          </CheckboxButton>
                        )}
                        <OptionLabel>{option.option_text}</OptionLabel>
                      </VotingOption>
                    );
                  })}
                </VotingOptions>

                <VoteActions>
                  <PrimaryButton
                    onClick={() => handleVote(poll.id)}
                    disabled={(selectedOptionsByPoll[poll.id] || []).length === 0 || isSubmitting}
                  >
                    {isSubmitting ? t("general.submitting") : t("conferences.submit_vote")}
                  </PrimaryButton>
                </VoteActions>
              </>
            )}

            {(poll.user_has_responded || poll.status === POLL_STATUS_ENDED) && poll.results && (
              <PollResultsDisplay 
                poll={poll} 
                variant="invitee" 
                showVoteLabel={true}
              />
            )}

            {poll.status === POLL_STATUS_ENDED && !poll.results && (
              <EndedMessage>{t("conferences.poll_ended")}</EndedMessage>
            )}
          </AttendeePollBody>
        </AttendeePollCard>
      ))}
    </AttendeeSection>
  );
};

export default InviteesPollView;

const AttendeeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 16px 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.5;
  margin: 0;
`;

const AttendeePollCard = styled.div`
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${({ $isEnded }) => ($isEnded ? 0.7 : 1)};
`;

const AttendeePollBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: ${({ theme }) => theme.menu.background};
`;

const AttendeePollQuestion = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
  line-height: 1.4;
  word-wrap: break-word;
`;

const AnswerTypeHint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
  font-style: italic;
`;

const VotingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VotingOption = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
  border-radius: 10px;
  background: ${({ theme, $selected }) => 
    $selected ? `${theme.menu.background}` : theme.menu.background};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
    background: ${({ theme, $selected }) => 
      $selected ? `${theme.menu.selectedTextColor}20` : `${theme.menu.selectedTextColor}08`};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.menu.background};
  transition: all 0.2s ease;
`;

const RadioDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.menu.selectedTextColor};
`;

const CheckboxButton = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.menu.background};
  transition: all 0.2s ease;
`;

const CheckIcon = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1;
`;

const OptionLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  flex: 1;
  line-height: 1.4;
`;

const VoteActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
`;

const EndedMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
  margin: 20px 0;
  font-style: italic;
`;